import { formComponents as fc } from "@/components/form";
import { ui } from "@/components/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useInsertSharedAttributes } from "../api/insertSharedAttributes";
import { useUpdateSharedAttributes } from "../api/updateSharedAttributes";
import type { SharedAttributeJSON } from "../types";
import UpdateAllCheckbox from "./UpdateAllCheckbox";

const formSchema = z.object({
	hospital_number: z.string().min(1, "Hospital number is required"),
});

interface Props {
	sharedAttributes?: SharedAttributeJSON;
	initialHospitalNumber?: string | null;
}

type FormSchemaType = z.infer<typeof formSchema>;

const SharedAttributeHospitalNumberForm = ({
	sharedAttributes,
	initialHospitalNumber,
}: Props) => {
	const { mutate: updateHospitalNumber } = useUpdateSharedAttributes();
	const { mutate: insertHospitalNumber } = useInsertSharedAttributes();
	const [updateAll, setUpdateAll] = useState(true);

	const hasAssociatedItems = Boolean(
		sharedAttributes?.events?.length ||
			sharedAttributes?.diaries?.length ||
			sharedAttributes?.trackers?.length,
	);

	const form = useForm<FormSchemaType>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			hospital_number: initialHospitalNumber ?? "",
		},
	});

	const handleSubmit: SubmitHandler<FormSchemaType> = (formData) => {
		if (sharedAttributes?.shared_attribute_id) {
			if (updateAll) {
				updateHospitalNumber({
					shared_attribute_id: sharedAttributes.shared_attribute_id,
					hospital_number: formData.hospital_number,
				});
			} else {
				insertHospitalNumber({
					hospital_number: formData.hospital_number,
				});
			}
		} else {
			insertHospitalNumber({
				hospital_number: formData.hospital_number,
			});
		}
	};

	return (
		<div className="space-y-4">
			<ui.Form {...form}>
				<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
					<fc.Input
						name="hospital_number"
						label="Hospital Number"
						placeholder="Enter hospital number"
						description="The hospital number helps identify the patient in the hospital system."
					/>
					<ui.DialogClose asChild>
						<ui.Button size="lg" type="submit">
							Save
						</ui.Button>
					</ui.DialogClose>
				</form>
			</ui.Form>
			{hasAssociatedItems && sharedAttributes && (
				<UpdateAllCheckbox checked={updateAll} onCheckedChange={setUpdateAll} />
			)}
		</div>
	);
};

export default SharedAttributeHospitalNumberForm;
